import {
  FaMapMarkerAlt,
  FaRegClock,
  FaStar,
  FaPlane,
  FaMountain,
  FaLeaf,
  FaRoute
} from "react-icons/fa";
import { IoMdArrowDropright } from "react-icons/io";
import { AppContext } from "../../App";
import { useContext } from "react";

import imageHero from "../../assets/kili2.jpg";
import imageDay1 from "../../assets/kilimanjaro.jpg";
import imageDay2 from "../../assets/beans.jpg";
import imageDay3 from "../../assets/summit.jpg";
import imageDay4 from "../../assets/kili.jpg";
import imageDay5 from "../../assets/mtkili.jpg";

const hero = {
  kicker: "8–9 DAYS Lemosho Route",
  title: "Scenic Western Ascent",
  subtitle: "Kilimanjaro’s most beautiful and least crowded route with elite summit success.",
  backgroundImage: imageHero,
  meta: ["8–9 Days", "High Success Rate", "Remote Trail", "Panoramic Views"],
};

const overview = `Lemosho is widely regarded as Kilimanjaro’s most scenic route. Starting from the remote western side, it offers diverse ecosystems, fewer crowds, and excellent acclimatization—making it one of the most successful routes to Uhuru Peak.`;

/* ================= THEME ================= */
const theme = {
  primary: "#166534",      // Deep green
  primaryLight: "#22c55e", // Vibrant green
  accent: "#14532d",
  bg: "#f0fdf4",
  cardBg: "#ffffff",
  text: "#1e293b",
  textMuted: "#64748b"
};


/* ================= HIGHLIGHTS ================= */
const highlights = [
  {
    icon: FaLeaf,
    title: "Untouched Rainforest",
    description: "Start in pristine western rainforest with rich biodiversity.",
    color: "#16a34a"
  },
  {
    icon: FaMountain,
    title: "Shira Plateau",
    description: "Expansive high-altitude plateau with dramatic open views.",
    color: "#0ea5e9"
  },
  {
    icon: FaRoute,
    title: "Superior Acclimatization",
    description: "Longer route profile improves summit success significantly.",
    color: "#f59e0b"
  },
  {
    icon: FaStar,
    title: "Premium Experience",
    description: "Fewer climbers, more space, and better overall trekking flow.",
    color: "#8b5cf6"
  },
];

/* ================= DETAILS ================= */
const details = [
  {
    icon: FaMapMarkerAlt,
    title: "Route",
    description: "Lemosho Gate → Shira → Barranco → Barafu → Uhuru → Mweka"
  },
  {
    icon: FaRegClock,
    title: "Duration",
    description: "8–9 days for optimal acclimatization"
  },
  {
    icon: FaStar,
    title: "Difficulty",
    description: "Moderate to strenuous with high summit success"
  },
  {
    icon: FaPlane,
    title: "Best For",
    description: "Scenery seekers + serious summit success goals"
  },
];

/* ================= ITINERARY ================= */
const itinerary = [
  {
    dayNum: "01",
    title: "Arrival in Moshi",
    image: imageDay1,
    body: "Arrival, briefing, and overnight in Moshi.",
    activities: "Airport transfer, rest",
    tags: ["Arrival", "Moshi"]
  },
  {
    dayNum: "02",
    title: "Lemosho Gate → Forest Camp",
    image: imageDay1,
    body: "Enter pristine rainforest and trek to Big Tree Camp.",
    activities: "3–4hrs • 6km • Rainforest",
    tags: ["Rainforest", "Wildlife"]
  },
  {
    dayNum: "03",
    title: "Forest → Shira 1",
    image: imageDay2,
    body: "Transition from forest to moorland with wide views.",
    activities: "5–6hrs • Moorland",
    tags: ["Moorland", "Views"]
  },
  {
    dayNum: "04",
    title: "Shira 1 → Shira 2",
    image: imageDay3,
    body: "Cross Shira Plateau with acclimatization walks.",
    activities: "6–7hrs • Plateau",
    tags: ["Plateau", "Acclimatization"]
  },
  {
    dayNum: "05",
    title: "Shira → Barranco via Lava Tower",
    image: imageDay4,
    body: "Climb high, descend low—critical acclimatization day.",
    activities: "6–8hrs • Semi-desert",
    tags: ["Lava Tower", "Acclimatization"]
  },
  {
    dayNum: "06",
    title: "Barranco → Karanga",
    image: imageDay5,
    body: "Climb Barranco Wall and traverse ridges.",
    activities: "4–5hrs • Alpine",
    tags: ["Barranco Wall"]
  },
  {
    dayNum: "07",
    title: "Karanga → Barafu",
    image: imageDay2,
    body: "Final camp before summit push.",
    activities: "4–5hrs • Alpine Desert",
    tags: ["Summit Prep"]
  },
  {
    dayNum: "08",
    title: "Summit → Mweka",
    image: imageDay3,
    body: "Midnight summit to Uhuru Peak then descend.",
    activities: "Long summit push",
    tags: ["Summit", "Uhuru Peak"]
  },
];

/* ================= COMPONENT ================= */
export default function Lemosho() {
  const appContext = useContext(AppContext);
  return (
    <div style={{ maxWidth: "1400px", margin: "0 auto", background: theme.bg }}>

      {/* HERO */}
      <section
        style={{
          height: "75vh",
          backgroundImage: `linear-gradient(135deg, rgba(3,20,10,0.85), rgba(22,101,52,0.75)), url(${hero.backgroundImage})`,
          backgroundSize: "cover",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "white",
          textAlign: "center"
        }}
      >
        <div>
          <span style={{ opacity: 0.85 }}>{hero.kicker}</span>
          <h1 style={{ fontSize: "3.5rem", margin: "1rem 0" }}>{hero.title}</h1>
          <p style={{ fontSize: "1.3rem", marginBottom: "2rem" }}>{hero.subtitle}</p>

          <div style={{ display: "flex", gap: "0.5rem", justifyContent: "center", flexWrap: "wrap" }}>
            {hero.meta.map((m, i) => (
              <span key={i} style={{
                background: "rgba(255,255,255,0.15)",
                padding: "0.4rem 1rem",
                borderRadius: "20px"
              }}>
                {m}
              </span>
            ))}
          </div>
        </div>
      </section>

      <div style={{ padding: "4rem 2rem" }}>

        {/* OVERVIEW */}
        <section style={{ textAlign: "center", marginBottom: "4rem" }}>
          <h2>Why Lemosho Route?</h2>
          <p style={{ maxWidth: "700px", margin: "auto" }}>{overview}</p>
        </section>

        {/* DETAILS */}
        <section style={{ display: "flex", gap: "1rem", flexWrap: "wrap", marginBottom: "4rem" }}>
          {details.map((d, i) => {
            const Icon = d.icon;
            return (
              <div key={i} style={{
                background: "white",
                padding: "1rem",
                borderRadius: "10px",
                display: "flex",
                gap: "0.7rem"
              }}>
                <Icon color={theme.primary} />
                <div>
                  <strong>{d.title}</strong>
                  <div>{d.description}</div>
                </div>
              </div>
            );
          })}
        </section>

        {/* HIGHLIGHTS */}
        <section style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(250px,1fr))", gap: "1rem" }}>
          {highlights.map((h, i) => {
            const Icon = h.icon;
            return (
              <div key={i} style={{ background: "white", padding: "1.5rem", borderRadius: "12px" }}>
                <Icon style={{ fontSize: "1.5rem", color: h.color }} />
                <h3>{h.title}</h3>
                <p>{h.description}</p>
              </div>
            );
          })}
        </section>

        {/* ITINERARY */}
        <section style={{ marginTop: "4rem" }}>
          <h2 style={{ textAlign: "center" }}>8-Day Scenic Journey</h2>

          {itinerary.map((day, i) => (
            <div key={i} style={{
              display: "flex",
              flexDirection: i % 2 ? "row-reverse" : "row",
              margin: "2rem 0",
              background: "white",
              borderRadius: "12px",
              overflow: "hidden"
            }}>
              <div style={{
                width: "40%",
                backgroundImage: `url(${day.image})`,
                backgroundSize: "cover"
              }} />
              <div style={{ padding: "1.5rem", width: "60%" }}>
                <h3>Day {day.dayNum}: {day.title}</h3>
                <p>{day.body}</p>
                <small>{day.activities}</small>
              </div>
            </div>
          ))}
        </section>

        {/* CTA */}
        <section style={{ textAlign: "center", marginTop: "4rem" }}>
          <h2>Climb the Most Scenic Route on Kilimanjaro</h2>
          <button onClick={() => appContext.navTo({ item: 'plan-trip' })} style={{
            background: theme.primary,
            color: "white",
            padding: "1rem 2rem",
            borderRadius: "30px",
            border: "none"
          }}>
            Book Lemosho <IoMdArrowDropright />
          </button>
        </section>

      </div>
    </div>
  );
}